import * as React from 'react';
import jwtDecode from 'jwt-decode';
import { AsyncStatus } from 'utils/asyncStatusHelper';
import { uploadImage, deleteImage, getAuthToken } from 'actions/fileUploadActions';
import * as addReviewAnalytics from 'pages/AddRatingAndReviewPage/AddRatingAndReviewAnalytics';
import AsyncTaskQueue from 'utils/SyncTask/AsyncTaskQueue';
function updateFileState(file, updater) {
    return (state) => ({
        files: state.files.map(f => {
            if (file === f.file) {
                return updater(f);
            }
            return f;
        })
    });
}
/**
 * References:
 * https://www.smashingmagazine.com/2018/01/drag-drop-file-uploader-vanilla-js/
 * http://blog.brew.com.hk/working-with-files-in-javascript/
 */
export class FileUploadProvider extends React.Component {
    constructor(props) {
        super(props);
        this.cancelTokens = new WeakMap();
        /**
         * Checks for `authToken`, if it does not exists, gets the token
         */
        this.checkAuthTokenAndCall = () => {
            return new Promise((resolve, reject) => {
                // check if we have a `authToken`
                // if we don't or we have an expired token, fetch a new token
                let isAuthTokenExpired = this.decodedAuthToken && this.decodedAuthToken.exp < Math.floor(Date.now() / 1000);
                if (isAuthTokenExpired || !this.authToken) {
                    getAuthToken()
                        .then(resp => {
                        this.authToken = resp.accessToken;
                        this.decodedAuthToken = jwtDecode(this.authToken);
                        resolve();
                    })
                        .catch(() => {
                        reject();
                    });
                }
                else {
                    resolve();
                }
            });
        };
        this.onFilesUpdate = () => {
            if (typeof this.props.onFilesUpdate === 'function') {
                this.props.onFilesUpdate(this.state.files.map(f => ({
                    name: f.file.name,
                    resourceId: f.resourceId || ''
                })));
            }
        };
        this.onAsyncStatusChange = () => {
            if (typeof this.props.onAsyncStatusChange === 'function') {
                this.props.onAsyncStatusChange(this.state.files.map(f => f.asyncStatus));
            }
        };
        /**
         * Takes a `FileList` (from a file input) and adds the file
         * with default metadata to the state.
         *
         * Also checks for `autoupload` prop and triggers upload if applicable
         */
        this.addFiles = (files) => {
            const filesArray = Array.from(files);
            const validFiles = this.props.getValidSelectedFiles(filesArray);
            addReviewAnalytics.trackAddImageClick();
            addReviewAnalytics.trackSelectSource();
            if (validFiles.length === 0) {
                return;
            }
            const existingFiles = this.state.files;
            const filesState = validFiles.map(file => ({
                file,
                progress: 0,
                asyncStatus: AsyncStatus.INIT
            }));
            this.setState(state => ({
                files: [...state.files, ...filesState]
            }), this.onAsyncStatusChange);
            Promise.all(Array.from(validFiles).map(file => {
                if (this.props.compressFile) {
                    return this.props.compressFile(file);
                }
                else {
                    return Promise.resolve(file);
                }
            })).then(newFiles => {
                const filesToUpload = Array.from(newFiles).map(file => {
                    const id = Math.random().toString(36).slice(7);
                    return {
                        file,
                        progress: 0,
                        asyncStatus: AsyncStatus.INIT,
                        id
                    };
                });
                this.setState(() => {
                    const newStateFiles = [...existingFiles, ...filesToUpload];
                    if (typeof this.props.onFilesAdd === 'function') {
                        this.props.onFilesAdd(filesToUpload);
                    }
                    if (this.props.autoUpload) {
                        this.uploadFiles(filesToUpload);
                    }
                    return {
                        files: newStateFiles
                    };
                });
            });
        };
        /**
         * Handles deleting a file from state and server (if applicable)
         */
        this.deleteFile = (index) => {
            const resourceId = this.state.files[index].resourceId;
            // If `resourceId` is found, then it means that the file is already uploaded
            // and we need to delete the image from server
            if (resourceId) {
                this.setState(state => ({
                    files: state.files.map(f => (Object.assign(Object.assign({}, f), { asyncStatus: f.resourceId === resourceId ? AsyncStatus.LOADING : f.asyncStatus })))
                }), this.onAsyncStatusChange);
                deleteImage(resourceId, this.authToken)
                    .then(() => {
                    this.setState(state => ({
                        files: state.files.filter(f => f.resourceId !== resourceId)
                    }), () => {
                        this.onFilesUpdate();
                        this.onAsyncStatusChange();
                    });
                    addReviewAnalytics.trackDeleteImage();
                })
                    .catch(() => {
                    this.handleFileUploadError(this.state.files[index].file, AsyncStatus.SUCCESS);
                    this.props.onFileUploadError && this.props.onFileUploadError();
                });
            }
            else {
                const file = this.state.files[0].file;
                const token = this.cancelTokens.get(file);
                // we find cancel token for given file, cancel the upload
                if (token && typeof token.cancel === 'function') {
                    token.cancel();
                }
                // we can simply remove the file from state
                this.setState(state => ({
                    files: state.files.filter((_, i) => i !== index)
                }), this.onFilesUpdate);
            }
        };
        /**
         * Handles uploading a `File` to the server and updating its progress
         */
        this.uploadFile = (file) => {
            const formData = new FormData();
            formData.append('file', file, file.name);
            this.setState(updateFileState(file, f => (Object.assign(Object.assign({}, f), { asyncStatus: AsyncStatus.LOADING }))), this.onAsyncStatusChange);
            const token = {};
            uploadImage(formData, file, this.authToken, p => {
                this.setState(updateFileState(file, f => (Object.assign(Object.assign({}, f), { asyncStatus: AsyncStatus.LOADING, progress: p }))));
            }, token, this.props.oneShot)
                .then(res => {
                this.setState(updateFileState(file, f => {
                    const updatedFile = Object.assign(Object.assign(Object.assign({}, f), { asyncStatus: AsyncStatus.SUCCESS, progress: 100 }), res);
                    if (typeof this.props.onFileUploadSuccess === 'function') {
                        this.props.onFileUploadSuccess(updatedFile);
                    }
                    return updatedFile;
                }), () => {
                    this.onFilesUpdate();
                    this.onAsyncStatusChange();
                });
                this.cancelTokens.delete(file);
            })
                .catch(() => {
                // handle api errors here
                this.setState(updateFileState(file, f => (Object.assign(Object.assign({}, f), { asyncStatus: AsyncStatus.ERROR }))));
                this.cancelTokens.delete(file);
                addReviewAnalytics.trackAddImageServerError();
            });
            this.cancelTokens.set(file, token);
        };
        /**
         * Method for uploading given files
         */
        this.uploadFiles = (files) => {
            files.forEach(f => {
                if (f.asyncStatus === AsyncStatus.INIT) {
                    this.handleFileUpload(f.file);
                }
            });
        };
        /**
         * Check for token and upload file
         */
        this.handleFileUpload = (file) => {
            this.asyncTaskHelper.addInQueue(() => {
                return this.checkAuthTokenAndCall()
                    .then(() => {
                    this.uploadFile(file);
                })
                    .catch(() => {
                    this.handleFileUploadError(file);
                    this.props.onFileUploadError && this.props.onFileUploadError();
                });
            });
        };
        /**
         * Check for token and delete file
         */
        this.handleFileDelete = (index) => {
            this.asyncTaskHelper.addInQueue(() => {
                return this.checkAuthTokenAndCall()
                    .then(() => {
                    this.deleteFile(index);
                })
                    .catch(() => {
                    this.handleFileUploadError(this.state.files[index].file);
                    this.props.onFileUploadError && this.props.onFileUploadError();
                });
            });
        };
        /**
         * Check for token and upload all the files
         */
        this.handleStartUpload = () => {
            this.asyncTaskHelper.addInQueue(() => {
                return this.checkAuthTokenAndCall()
                    .then(() => {
                    this.uploadFiles(this.state.files);
                })
                    .catch(() => {
                    this.state.files.forEach(f => this.handleFileUploadError(f.file));
                    this.props.onFileUploadError && this.props.onFileUploadError();
                });
            });
        };
        /**
         * Helper for retrying upload
         */
        this.handleRetry = (file) => {
            if (file.asyncStatus === AsyncStatus.LOADING || file.asyncStatus === AsyncStatus.SUCCESS)
                return;
            this.handleFileUpload(file.file);
        };
        this.asyncTaskHelper = new AsyncTaskQueue(1, 'fileUpload');
        this.state = {
            files: []
        };
    }
    /**
     * Method for handling error state of files
     */
    handleFileUploadError(file, asyncStatus = AsyncStatus.ERROR) {
        this.setState(updateFileState(file, f => (Object.assign(Object.assign({}, f), { asyncStatus }))));
    }
    render() {
        return this.props.render({
            handleAddFiles: this.addFiles,
            files: this.state.files,
            deleteFile: this.handleFileDelete,
            startUpload: this.handleStartUpload,
            retryUpload: this.handleRetry
        });
    }
}
FileUploadProvider.defaultProps = {
    maxFilesAllowed: 5
};
